import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiUser, FiBriefcase, FiAlertCircle, FiDollarSign } from 'react-icons/fi';
import { useVendedor } from '../../hooks/useVendedores';
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';

const VendedorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    vendedor,
    isEditing,
    isLoading,
    error,
    hasChanges,
    updateVendedorData,
    saveVendedor,
    resetVendedor,
    clearError
  } = useVendedor(id);

  const [validationErrors, setValidationErrors] = useState({});
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Opciones para los selectores
  const roles = [
    { value: 'sales', label: 'VENDEDOR' },
    { value: 'sales_manager', label: 'GERENTE DE VENTAS' },
    { value: 'manager', label: 'GERENTE' },
    { value: 'admin', label: 'ADMINISTRADOR' },
    { value: 'support', label: 'SOPORTE' }
  ];

  const frecuenciasPago = [
    { value: 'WEEKLY', label: 'SEMANAL' },
    { value: 'BIWEEKLY', label: 'CADA 15 DÍAS' },
    { value: 'MONTHLY', label: 'CADA FINAL DE MES' },
    { value: 'OTHER', label: 'OTRO' }
  ];

  const metodosPago = [
    { value: 'ZELLE', label: 'ZELLE' },
    { value: 'CHECK', label: 'CHEQUE' },
    { value: 'BANK_TRANSFER', label: 'DEPÓSITO/TRANSFERENCIA' },
    { value: 'CASH', label: 'EFECTIVO' },
    { value: 'OTHER', label: 'OTROS' }
  ];

  const paises = [
    { value: 'Colombia', label: 'Colombia' },
    { value: 'Estados Unidos', label: 'Estados Unidos' },
    { value: 'Canadá', label: 'Canadá' },
    { value: 'México', label: 'México' }
  ];

  const idiomas = [
    { value: 'es', label: 'Español' },
    { value: 'en', label: 'English' }
  ];

  const timezones = [
    { value: 'America/Bogota', label: 'Bogotá (GMT-5)' },
    { value: 'America/New_York', label: 'New York (GMT-5)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8)' },
    { value: 'America/Mexico_City', label: 'Ciudad de México (GMT-6)' }
  ];

  // Limpiar errores cuando se monta el componente
  useEffect(() => {
    clearError();
    setValidationErrors({});
  }, [clearError]);

  const handleInputChange = (field, value) => {
    updateVendedorData(field, value);
    
    // Lógica especial para comisión
    if (field === 'commissionPercentage') {
      // Asegurar que esté entre 0 y 100
      const numValue = parseFloat(value);
      if (numValue > 100) {
        updateVendedorData(field, 100);
      } else if (numValue < 0) {
        updateVendedorData(field, 0);
      }
    }
    
    // Limpiar error de validación del campo específico
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSaving(true);
    setValidationErrors({});
    
    try {
      const result = await saveVendedor();

      if (result.success) {
        // Navegar de vuelta a la lista con mensaje de éxito
        navigate('/system/vendedores', {
          state: {
            message: result.message,
            type: 'success'
          }
        });
      } else {
        // Procesar errores de validación
        const processedErrors = {};
        
        // Si hay validation_errors (array format)
        if (result.validation_errors && Array.isArray(result.validation_errors)) {
          result.validation_errors.forEach(err => {
            processedErrors[err.field] = err.message;
          });
        }
        
        // Si hay validationErrors (object format)
        if (result.validationErrors && typeof result.validationErrors === 'object') {
          Object.assign(processedErrors, result.validationErrors);
        }
        
        // Si no hay errores específicos pero hay error general
        if (Object.keys(processedErrors).length === 0 && result.error) {
          processedErrors.general = result.error;
        }
        
        setValidationErrors(processedErrors);
      }
    } catch (err) {
      setValidationErrors({ 
        general: 'Error inesperado al guardar el vendedor' 
      });
      console.error('Unexpected error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      navigate('/system/vendedores');
    }
  };

  const confirmCancel = () => {
    resetVendedor();
    navigate('/system/vendedores');
  };

  if (isLoading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner-single" style={{ width: '40px', height: '40px' }}></div>
        <div className="loading-message">
          {isEditing ? 'Cargando vendedor...' : 'Preparando formulario...'}
        </div>
      </div>
    );
  }

  return (
    <div className="cliente-form-page">
      {/* Header */}
      <div className="form-header">
        <button 
          className="btn btn--ghost"
          onClick={handleCancel}
          type="button"
        >
          <FiArrowLeft /> Volver a Vendedores
        </button>
        
        <h1 className="form-title">
          {isEditing ? 'EDITAR VENDEDOR' : 'NUEVO VENDEDOR'}
        </h1>
        
        <div className="form-actions">
          <button 
            className="btn btn--outline"
            onClick={handleCancel}
            type="button"
            disabled={isSaving}
          >
            <FiX /> Cancelar
          </button>
          
          <button 
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isSaving || !hasChanges}
            type="submit"
          >
            {isSaving ? (
              <>
                <div className="loading-spinner-single" style={{ width: '16px', height: '16px', marginRight: '8px' }}></div>
                Guardando...
              </>
            ) : (
              <>
                <FiSave /> {isEditing ? 'Actualizar' : 'Crear'} Vendedor
              </>
            )}
          </button>
        </div>
      </div>

      {/* Errores generales */}
      {(error || validationErrors.general) && (
        <div className="alert alert--danger">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error || validationErrors.general}
          </div>
        </div>
      )}

      {/* Errores de validación específicos */}
      {Object.keys(validationErrors).length > 1 || (Object.keys(validationErrors).length === 1 && !validationErrors.general) ? (
        <div className="alert alert--warning">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Errores de validación:</strong>
            <ul className="error-list">
              {Object.entries(validationErrors)
                .filter(([field]) => field !== 'general')
                .map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))
              }
            </ul>
          </div>
        </div>
      ) : null}

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="cliente-form">
        <div className="form-columns">
          {/* Columna Izquierda */}
          <div className="form-column">
            {/* Sección: Información Personal */}
            <div className="form-section">
              <div className="section-header">
                <FiUser className="section-icon" />
                <h2 className="section-title">Información Personal</h2>
              </div>
              
              <div className="form-group">
                <label className="form-label required">Nombre Completo</label>
                <input
                  type="text"
                  className={`form-input ${validationErrors.fullName ? 'error' : ''}`}
                  value={vendedor.fullName || ''}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  placeholder="Ingrese el nombre completo"
                  maxLength={255}
                />
                {validationErrors.fullName && (
                  <span className="form-error">{validationErrors.fullName}</span>
                )}
              </div>
              
              <div className="form-group">
                <label className="form-label required">Email</label>
                <input
                  type="email"
                  className={`form-input ${validationErrors.email ? 'error' : ''}`}
                  value={vendedor.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="correo@ejemplo.com"
                />
                {validationErrors.email && (
                  <span className="form-error">{validationErrors.email}</span>
                )}
              </div>
              
              <div className="form-group">
                <label className="form-label required">Teléfono</label>
                <input
                  type="tel"
                  className={`form-input ${validationErrors.phone ? 'error' : ''}`}
                  value={vendedor.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="(000) 000-0000"
                />
                {validationErrors.phone && (
                  <span className="form-error">{validationErrors.phone}</span>
                )}
              </div>
              
              <div className="form-group">
                <label className="form-label">Dirección</label>
                <textarea
                  className="form-input"
                  value={vendedor.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Dirección completa"
                  rows={3}
                />
              </div>

              {!isEditing && (
                <div className="form-group">
                  <label className="form-label required">Contraseña</label>
                  <div className="password-input-container">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`form-input ${validationErrors.password ? 'error' : ''}`}
                      value={vendedor.password || ''}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Contraseña (mínimo 8 caracteres)"
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <span className="form-error">{validationErrors.password}</span>
                  )}
                </div>
              )}
            </div>

            {/* Sección: Información Bancaria */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Información Bancaria</h3>
              </div>
              
              <div className="form-group">
                <label className="form-label">Nombre del Banco</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.bankName || ''}
                  onChange={(e) => handleInputChange('bankName', e.target.value)}
                  placeholder="Nombre del banco"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Número de Cuenta</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.bankAccount || ''}
                  onChange={(e) => handleInputChange('bankAccount', e.target.value)}
                  placeholder="Número de cuenta"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Routing Number</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.bankRouting || ''}
                  onChange={(e) => handleInputChange('bankRouting', e.target.value)}
                  placeholder="Routing number"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Código SWIFT</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.bankSwift || ''}
                  onChange={(e) => handleInputChange('bankSwift', e.target.value)}
                  placeholder="Código SWIFT"
                />
              </div>
            </div>

            {/* Sección: Configuración */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Configuración</h3>
              </div>
              
              <div className="form-group">
                <label className="form-label">Zona Horaria</label>
                <select
                  className="form-input"
                  value={vendedor.timezone || 'America/Bogota'}
                  onChange={(e) => handleInputChange('timezone', e.target.value)}
                >
                  {timezones.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Idioma</label>
                <select
                  className="form-input"
                  value={vendedor.language || 'es'}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                >
                  {idiomas.map(lang => (
                    <option key={lang.value} value={lang.value}>{lang.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Columna Derecha */}
          <div className="form-column">
            {/* Sección: Rol y Permisos */}
            <div className="form-section">
              <div className="section-header">
                <FiBriefcase className="section-icon" />
                <h2 className="section-title">Rol y Permisos</h2>
              </div>
              
              <div className="form-group">
                <label className="form-label required">Rol</label>
                <select
                  className={`form-input ${validationErrors.role ? 'error' : ''}`}
                  value={vendedor.role || 'SALES'}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                >
                  {roles.map(rol => (
                    <option key={rol.value} value={rol.value}>{rol.label}</option>
                  ))}
                </select>
                {validationErrors.role && (
                  <span className="form-error">{validationErrors.role}</span>
                )}
              </div>
              
              <div className="form-group">
                <div className="form-radio-group">
                  <label className="form-radio">
                    <input
                      type="radio"
                      name="activo"
                      value="true"
                      checked={vendedor.isActive !== false}
                      onChange={(e) => handleInputChange('isActive', true)}
                    />
                    <span className="radio-custom"></span>
                    ACTIVO
                  </label>
                  
                  <label className="form-radio">
                    <input
                      type="radio"
                      name="activo"
                      value="false"
                      checked={vendedor.isActive === false}
                      onChange={(e) => handleInputChange('isActive', false)}
                    />
                    <span className="radio-custom"></span>
                    INACTIVO
                  </label>
                </div>
              </div>
            </div>

            {/* Sección: Información de Comisiones */}
            <div className="form-section">
              <div className="section-header">
                <FiDollarSign className="section-icon" />
                <h3 className="section-title">Comisiones y Pagos</h3>
              </div>
              
              <div className="form-group">
                <label className="form-label">Porcentaje de Comisión (%)</label>
                <input
                  type="number"
                  className="form-input"
                  value={vendedor.commissionPercentage || ''}
                  onChange={(e) => handleInputChange('commissionPercentage', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  min="0"
                  max="100"
                  step="0.01"
                />
                <small className="form-help">Porcentaje de comisión sobre las ventas (0-100%)</small>
              </div>
              
              <div className="form-group">
                <label className="form-label">Frecuencia de Pago</label>
                <select
                  className="form-input"
                  value={vendedor.paymentPeriod || 'MONTHLY'}
                  onChange={(e) => handleInputChange('paymentPeriod', e.target.value)}
                >
                  {frecuenciasPago.map(freq => (
                    <option key={freq.value} value={freq.value}>{freq.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Forma de Pago</label>
                <select
                  className="form-input"
                  value={vendedor.paymentMethod || 'BANK_TRANSFER'}
                  onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                >
                  {metodosPago.map(metodo => (
                    <option key={metodo.value} value={metodo.value}>{metodo.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sección: Información Adicional */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Información Adicional</h3>
              </div>
              
              <div className="form-group">
                <label className="form-label">ID de Empleado</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.employeeId || ''}
                  onChange={(e) => handleInputChange('employeeId', e.target.value)}
                  placeholder="ID único del empleado"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Fecha de Contratación</label>
                <input
                  type="date"
                  className="form-input"
                  value={vendedor.hireDate || ''}
                  onChange={(e) => handleInputChange('hireDate', e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Notas</label>
                <textarea
                  className="form-input"
                  value={vendedor.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Notas adicionales sobre el vendedor..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Modal de confirmación para cambios no guardados */}
      <ConfirmModal
        isOpen={showUnsavedChangesModal}
        onClose={() => setShowUnsavedChangesModal(false)}
        onConfirm={confirmCancel}
        title="¿Descartar cambios?"
        message="Tienes cambios sin guardar. ¿Estás seguro de que quieres salir sin guardar?"
        confirmText="Sí, descartar"
        cancelText="Continuar editando"
        type="warning"
      />
    </div>
  );
};

export default VendedorForm;